import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import NewsListItem from "@/components/base/NewsListItem";
import { searchContent } from "@/utils/content";
import { movies } from "@/mocks/movies";

type Tab = "all" | "movie" | "news";

export default function Search() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const keyword = params.get("keyword") || "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<Tab>("all");

  const { movies: matchedMovies, news: matchedNews } = searchContent(keyword);
  const total = matchedMovies.length + matchedNews.length;
  const hotMovies = movies.filter((m) => m.hot).slice(0, 5);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    if (!kw) return;
    navigate(`/search?keyword=${encodeURIComponent(kw)}`);
  };

  const showMovies = tab === "all" || tab === "movie";
  const showNews = tab === "all" || tab === "news";

  const tabBtn = (key: Tab, label: string, count: number) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
        tab === key
          ? "bg-primary-500 text-background-50"
          : "bg-background-200 text-foreground-700 hover:bg-background-300"
      }`}
    >
      {label} ({count})
    </button>
  );

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">搜索</h1>

      <form
        onSubmit={submit}
        className="mt-4 flex items-center bg-background-50 border border-background-300 rounded-lg overflow-hidden max-w-2xl focus-within:ring-2 focus-within:ring-accent-400/60"
      >
        <span className="pl-4 text-foreground-400 flex items-center justify-center w-6 h-6">
          <i className="ri-search-line text-lg"></i>
        </span>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="输入影视名称、类型、演员或资讯关键词"
          className="flex-1 bg-transparent px-3 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
        />
        <button
          type="submit"
          className="m-1.5 px-5 py-2 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {keyword ? (
        <>
          <p className="mt-4 text-sm text-foreground-600">
            关键词「<span className="text-accent-600 font-semibold">{keyword}</span>」共找到{" "}
            <span className="text-accent-600 font-semibold">{total}</span> 条相关内容
          </p>

          {total > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {tabBtn("all", "全部", total)}
              {tabBtn("movie", "影视", matchedMovies.length)}
              {tabBtn("news", "资讯", matchedNews.length)}
            </div>
          )}

          {total === 0 ? (
            <div className="mt-8 rounded-lg bg-background-50 border border-background-200 py-14 flex flex-col items-center">
              <i className="ri-search-eye-line text-5xl text-background-400"></i>
              <p className="mt-4 text-sm text-foreground-600">未搜索到相关内容</p>
              <p className="mt-1 text-xs text-foreground-500">试试其他关键词，或看看下面的热门推荐</p>
              <div className="mt-6 w-full max-w-3xl px-6">
                <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-3 text-center">
                  热门影视推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {hotMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-10">
              {showMovies && matchedMovies.length > 0 && (
                <section>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 flex items-center gap-2 mb-4">
                    <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                    影视结果
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                    {matchedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </section>
              )}

              {showNews && matchedNews.length > 0 && (
                <section>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 flex items-center gap-2 mb-4">
                    <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                    资讯结果
                  </h2>
                  <div className="flex flex-col gap-3">
                    {matchedNews.map((n) => (
                      <NewsListItem key={n.id} item={n} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="mt-8">
          <p className="text-sm text-foreground-600">输入关键词开始搜索影视或资讯内容。</p>
          <h3 className="mt-6 text-sm font-heading font-semibold text-foreground-950 mb-3">
            热门影视推荐
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {hotMovies.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}